// The claim form's send logic, kept apart from the page so it can be tested in
// this package's plain-node suite (no DOM here). ClaimFormPage.handleSubmit is
// exactly: claimFormError → claimRequestBody → postClaim.
//
// THE BOT-CHECK TOKEN NEVER HOLDS A SUBMIT BACK (cca-8zf3, bead cca-r11uc).
// `claimFormError` does not take the token at all, and `claimRequestBody` sends
// whatever the widget has, '' included. The door decides: under advisory it
// lets every claim through, and even when enforcing it lets one through when
// Turnstile itself is down or unconfigured (ADR-072).

export interface ClaimantFields {
  fullName: string;
  email: string;
  phone: string;
  bankAccount: string;
  bankAccountName: string;
}

export interface Expense {
  date: string;
  activity: string;
  supplier: string;
  description: string;
  amount: string;
  gst: 'yes' | 'no' | '';
  receiptBase64: string;
  receiptFileName: string;
  receiptMimeType: string;
}

// NZ bank account: XX-XXXX-XXXXXXX-XX (2-4-7-2 digits, hyphens required)
const NZ_BANK_RE = /^\d{2}-\d{4}-\d{7}-\d{2}$/;

/** One receipt file, at most 4 MB (the page's own cap), as a base64 length.
 *  The SAME number the Finance Worker refuses above (cca-xero-worker
 *  src/reimburse/claim-shape.ts MAX_RECEIPT_BASE64_LENGTH): kept equal by hand,
 *  because the web package cannot import the worker (ARCH-2). */
export const MAX_RECEIPT_BASE64_LENGTH = 4 * Math.ceil((4 * 1024 * 1024) / 3);

/** The receipt types the server takes: what this form's picker accepts
 *  (`image/*,application/pdf`). A picker's `accept` is only a hint, so it is
 *  checked here before the send. */
const RECEIPT_TYPE = /^(image\/[a-z0-9][a-z0-9.+-]*|application\/pdf)$/i;

/** What stops the form sending, in the words shown to the claimant, or null
 *  when it may send. `today` is the NZ date (YYYY-MM-DD) an expense may not
 *  pass. It reads no bot-check token, by design (see the header). */
export function claimFormError(
  claimant: ClaimantFields,
  expenses: readonly Expense[],
  declaration: boolean,
  today: string,
): string | null {
  if (!claimant.fullName.trim()) return 'Full name is required';
  if (!claimant.email.trim() || !claimant.email.includes('@')) {
    return 'Valid email is required';
  }
  if (claimant.bankAccount.trim() && !NZ_BANK_RE.test(claimant.bankAccount.trim())) {
    return 'Bank account must be in NZ format: XX-XXXX-XXXXXXX-XX (e.g. 12-3456-7890123-00)';
  }
  for (const [i, e] of expenses.entries()) {
    if (!e.amount || isNaN(parseFloat(e.amount)) || parseFloat(e.amount) <= 0) {
      return `Expense ${i + 1}: amount must be a positive number`;
    }
    if (e.date && e.date > today) return `Expense ${i + 1}: date cannot be in the future`;
    if (e.receiptBase64 && !RECEIPT_TYPE.test(e.receiptMimeType)) {
      return `Expense ${i + 1}: the receipt must be a photo or a PDF`;
    }
    if (e.receiptBase64.length > MAX_RECEIPT_BASE64_LENGTH) {
      return `Expense ${i + 1}: the receipt is larger than 4 MB. Please attach a smaller file`;
    }
  }
  if (!declaration) return 'Please agree to the declaration';
  return null;
}

/** The JSON body POST /api/reimburse/submit takes. */
export interface ClaimRequestBody {
  idempotencyToken: string;
  // `| undefined` throughout: an empty field is sent as undefined, which
  // JSON.stringify leaves out (exactOptionalPropertyTypes).
  claimant: {
    fullName: string;
    email: string;
    phone?: string | undefined;
    bankAccount?: string | undefined;
    bankAccountName?: string | undefined;
    declarationAgreed: true;
  };
  expenses: {
    date?: string | undefined;
    activity?: string | undefined;
    supplier?: string | undefined;
    description?: string | undefined;
    amount: number;
    gst?: string | undefined;
    receiptBase64?: string | undefined;
    receiptFileName?: string | undefined;
    receiptMimeType?: string | undefined;
  }[];
  /** The widget's token, or '' when it has none: sent either way. */
  turnstileToken: string;
}

export function claimRequestBody(
  claimant: ClaimantFields,
  expenses: readonly Expense[],
  idempotencyToken: string,
  turnstileToken: string,
): ClaimRequestBody {
  return {
    idempotencyToken,
    claimant: {
      fullName: claimant.fullName.trim(),
      email: claimant.email.trim(),
      phone: claimant.phone.trim() || undefined,
      bankAccount: claimant.bankAccount.trim() || undefined,
      bankAccountName: claimant.bankAccountName.trim() || undefined,
      declarationAgreed: true,
    },
    expenses: expenses.map((e) => ({
      date: e.date || undefined,
      activity: e.activity || undefined,
      supplier: e.supplier.trim() || undefined,
      description: e.description.trim() || undefined,
      amount: parseFloat(e.amount),
      gst: e.gst || undefined,
      receiptBase64: e.receiptBase64 || undefined,
      receiptFileName: e.receiptFileName || undefined,
      receiptMimeType: e.receiptMimeType || undefined,
    })),
    turnstileToken,
  };
}

export type ClaimPostResult = { ok: true; submissionId: string } | { ok: false; message: string };

/**
 * Send the claim. A refusal is answered in the door's own words when it gives
 * them (`message`: the 429 "too many claims", the 503 "cannot be accepted for a
 * moment", the 403 bot check), otherwise by its error code, as before. A network
 * failure throws, for the page to show.
 */
export async function postClaim(
  apiBase: string,
  body: ClaimRequestBody,
  fetchFn: typeof fetch = fetch,
): Promise<ClaimPostResult> {
  const res = await fetchFn(`${apiBase}/api/reimburse/submit`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
    body: JSON.stringify(body),
  });
  const json = (await res.json()) as {
    ok?: boolean;
    submissionId?: string;
    error?: string;
    message?: string;
  };
  if (!res.ok || !json.ok) {
    return { ok: false, message: json.message ?? json.error ?? `Server error ${res.status}` };
  }
  return { ok: true, submissionId: json.submissionId ?? 'submitted' };
}
